import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { API_URL } from '../config/api';
import { Info } from 'lucide-react';
import CcEmailsInput from './CcEmailsInput';
import './AddPortfolioPropertyModal.css';

const DigestSettingsModal = ({ isOpen, onClose }) => {
    const { session } = useAuth();
    const { showToast } = useToast();

    const [enabled, setEnabled] = useState(true);
    const [freq, setFreq] = useState('monthly');
    const [ccEmails, setCcEmails] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isOpen || !session?.access_token) return;
        setLoading(true);
        fetch(`${API_URL}/portfolio/digest-settings`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
        })
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (data) {
                    setEnabled(data.enabled);
                    setFreq(data.email_frequency);
                    setCcEmails(data.cc_emails || []);
                }
            })
            .finally(() => setLoading(false));
    }, [isOpen, session]);

    if (!isOpen) return null;

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/portfolio/digest-settings`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    enabled,
                    email_frequency: freq,
                    cc_emails: ccEmails,
                }),
            });
            if (res.ok) {
                showToast('Digest settings saved', 'success');
                onClose();
            } else {
                showToast('Failed to save', 'error');
            }
        } catch {
            showToast('Network error', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="portfolio-modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">Portfolio Digest</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-content-scroll">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>Loading...</div>
                    ) : (
                        <div className="pm-step-content">
                            <p className="notification-hint" style={{ margin: 0 }}>
                                <Info size={13} />
                                Receive a periodic email summarizing all your tracked properties — current ARV, list price, rent estimate, and ROI.
                            </p>

                            <label className="checkbox-label" style={{ marginTop: 12 }}>
                                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                                Enable portfolio digest
                            </label>

                            {enabled && (
                                <div className="notification-row" style={{ marginTop: 12 }}>
                                    <div className="notification-field">
                                        <label className="form-label">Frequency</label>
                                        <select className="std-select" value={freq} onChange={(e) => setFreq(e.target.value)}>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="never">Never</option>
                                        </select>
                                    </div>
                                    <div className="notification-field">
                                        <CcEmailsInput
                                            value={ccEmails}
                                            onChange={setCcEmails}
                                            placeholder="partner@example.com"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DigestSettingsModal;
